import type { PricingConfig } from '@lbhl/shared';
import type { StoredBid } from './db.js';

/**
 * API client.
 *
 * Every call can fail because the device is offline, and that is a normal
 * condition here rather than an error state — callers fall back to the cache.
 */

const BASE = '/api';
const TOKEN_KEY = 'lbhl.session';

export class ApiError extends Error {
  readonly status: number;
  /** True when the server said no in a way that retrying will not fix. */
  readonly permanent: boolean;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.permanent = status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
  }
}

export class OfflineError extends Error {
  constructor() {
    super('No connection.');
    this.name = 'OfflineError';
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private windows and blocked site data both throw here.
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* session simply will not persist across reloads; not fatal */
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  let res: Response;

  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        // Only declare a JSON body when there actually is one. Fastify rejects
        // `content-type: application/json` with an empty body -- "Body cannot be
        // empty when content-type is set to 'application/json'" -- which broke
        // every bodyless POST, such as publishing a draft.
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new OfflineError();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error ?? res.statusText);
  }

  return res.json() as Promise<T>;
}

export interface SessionUser {
  id: number;
  email: string;
  name: string;
  role: 'estimator' | 'admin';
}

export interface Session {
  token: string;
  user: SessionUser;
}

export async function login(email: string, password: string): Promise<Session> {
  return request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
}

/** Claims an invitation with its one-time code and sets the password. */
export async function enroll(input: {
  email: string;
  enrollmentCode: string;
  password: string;
}): Promise<Session> {
  return request('/auth/enroll', { method: 'POST', body: JSON.stringify(input) });
}

export async function googleLogin(idToken: string): Promise<Session> {
  return request('/auth/google', { method: 'POST', body: JSON.stringify({ idToken }) });
}

/**
 * What the login screen needs, without a session.
 *
 * Separate from `fetchRuntimeConfig` because that one requires a token, and a
 * screen whose job is to obtain a token cannot use it.
 */
export interface AuthConfig {
  accessModel: 'invite-only';
  localLoginEnabled: boolean;
  /** Null when Google sign-in is not configured, so the button is not offered. */
  googleClientId: string | null;
}

export async function fetchAuthConfig(): Promise<AuthConfig> {
  return request('/auth/config');
}

export async function fetchMe(): Promise<SessionUser> {
  return request('/auth/me');
}

export async function fetchPublishedPricing(): Promise<PricingConfig> {
  return request('/pricing/published');
}

export interface SyncOutcome {
  id: string;
  created: boolean;
  needsReview: boolean;
  reviewNote: string | null;
  totalCents: number;
}

export async function pushBid(bid: StoredBid): Promise<SyncOutcome> {
  return request(`/bids/${bid.id}`, {
    method: 'PUT',
    body: JSON.stringify({
      pricingVersionId: bid.pricingVersionId,
      engineVersion: bid.engineVersion,
      input: bid.input,
      result: bid.result,
      status: bid.status,
      // Omitted entirely when blank, so the server does not create an empty
      // customer row for a bid the estimator has not named yet.
      ...(bid.customer.name.trim() ? { customer: bid.customer } : {}),
      ...(bid.customer.name.trim() && bid.property.addressLine1.trim()
        ? { property: bid.property }
        : {}),
    }),
  });
}

// --- admin: pricing versions ------------------------------------------------

export interface PricingVersionRow {
  id: number;
  label: string;
  status: 'draft' | 'published' | 'archived';
  rate_per_foot_cents: number;
  price_per_strand_cents: number;
  travel_rate_cents_per_mile: number;
  travel_free_radius_miles: string;
  travel_basis: 'one_way' | 'round_trip';
  created_at: string;
  published_at: string | null;
}

export interface RateChange {
  field: string;
  label: string;
  from: string;
  to: string;
}

export async function fetchPricingVersions(): Promise<PricingVersionRow[]> {
  return request('/pricing/versions');
}

export async function createDraft(label: string): Promise<{ draftId: number; clonedFrom: number }> {
  return request('/pricing/drafts', { method: 'POST', body: JSON.stringify({ label }) });
}

export interface DraftPatchBody {
  ratePerFootCents?: number;
  pricePerStrandCents?: number;
  travelRateCentsPerMile?: number;
  travelFreeRadiusMiles?: number;
  travelBasis?: 'one_way' | 'round_trip';
}

export async function patchDraft(draftId: number, patch: DraftPatchBody): Promise<void> {
  await request(`/pricing/drafts/${draftId}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function patchDraftInventory(
  draftId: number,
  sku: string,
  unitCostCents: number,
): Promise<void> {
  await request(`/pricing/drafts/${draftId}/inventory/${encodeURIComponent(sku)}`, {
    method: 'PATCH',
    body: JSON.stringify({ unitCostCents }),
  });
}

export async function fetchDraftDiff(
  draftId: number,
): Promise<{ fromVersionId: number; changes: RateChange[] }> {
  return request(`/pricing/drafts/${draftId}/diff`);
}

export async function publishDraftVersion(
  draftId: number,
): Promise<{ publishedId: number; archivedId: number | null }> {
  return request(`/pricing/drafts/${draftId}/publish`, { method: 'POST' });
}

// --- admin: review queue ----------------------------------------------------

export interface ReviewRow {
  id: string;
  total_cents: number;
  needs_review: boolean;
  review_note: string | null;
  engine_version: string;
  pricing_version_id: number;
  selected_package: string | null;
  created_at: string;
  created_by_email: string;
  pricing_label: string;
  /** Quoted offline against pricing that was already superseded at sync time. */
  quoted_on_superseded: boolean;
}

export interface ReviewQueue {
  counts: { needs_review: number; quoted_on_superseded: number };
  bids: ReviewRow[];
}

export async function fetchReviewQueue(): Promise<ReviewQueue> {
  return request('/bids/review');
}

/** A specific version's full config — used to show a draft's own material costs. */
export async function fetchPricingVersionConfig(id: number): Promise<PricingConfig> {
  return request(`/pricing/versions/${id}`);
}

// --- admin: users -----------------------------------------------------------

export interface UserRow {
  id: number;
  email: string;
  name: string;
  role: 'estimator' | 'admin';
  active: boolean;
  /** Invited but has never signed in. */
  pending: boolean;
  bidCount: number;
  lastSeenAt: string | null;
  createdAt: string;
  /** Unclaimed and past its expiry — must be re-issued before it can be used. */
  inviteExpired: boolean;
  /** Not on the organisation's own Workspace domain. Allowed; worth seeing. */
  external: boolean;
  inviteExpiresAt: string | null;
  claimedAt: string | null;
  hd: string | null;
}

export async function fetchUsers(): Promise<UserRow[]> {
  return request('/users');
}

export interface RuntimeConfig {
  /** Always 'invite-only': the users list is the allowlist. */
  accessModel: 'invite-only';
  /** The organisation's own domain, for labelling internal vs external. Not a gate. */
  internalHdDomain: string | null;
  inviteTtlHours: number;
  googleSignInConfigured: boolean;
  sessionTtlDays: number;
  deliveryAdapter: string;
}

export async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  return request('/config');
}

export async function inviteUser(
  email: string,
  name: string,
  role: 'estimator' | 'admin',
): Promise<UserRow & { reissued: boolean; enrollmentCode: string }> {
  return request('/users', { method: 'POST', body: JSON.stringify({ email, name, role }) });
}

/**
 * Resets someone's credentials and returns a fresh enrollment code.
 *
 * Shown once and unrecoverable afterwards, so the UI must display it rather than
 * assume it can be fetched again.
 */
export async function resetCredentials(
  userId: number,
): Promise<UserRow & { enrollmentCode: string }> {
  return request(`/users/${userId}/reset-credentials`, { method: 'POST' });
}

/** Revokes an unclaimed invitation. Claimed accounts use removeUser instead. */
export async function revokeInvite(id: number): Promise<{ revoked: number }> {
  return request(`/users/${id}/invite`, { method: 'DELETE' });
}

export async function updateUser(
  id: number,
  patch: { role?: 'estimator' | 'admin'; active?: boolean },
): Promise<UserRow> {
  return request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export type RemoveUserOutcome =
  | { action: 'deleted'; email: string }
  | { action: 'deactivated'; email: string; bidCount: number; reason: string };

export async function removeUser(id: number): Promise<RemoveUserOutcome> {
  return request(`/users/${id}`, { method: 'DELETE' });
}

// --- admin: cost inputs -----------------------------------------------------

export type CostDriver =
  | 'per_measured_foot'
  | 'per_strand'
  | 'per_run'
  | 'per_bid'
  /** Quantity comes from the bid's Engineering counts, not from config. */
  | 'manual_entry';

export interface NewCostItemBody {
  name: string;
  category: string;
  uom: 'foot' | 'unit';
  unitCostCents: number;
  driver: CostDriver;
  qtyPerDriver: number;
}

export async function addCostItem(draftId: number, item: NewCostItemBody): Promise<{ sku: string }> {
  return request(`/pricing/drafts/${draftId}/inventory`, {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

export async function removeCostItem(draftId: number, sku: string): Promise<void> {
  await request(`/pricing/drafts/${draftId}/inventory/${encodeURIComponent(sku)}`, {
    method: 'DELETE',
  });
}

export async function discardDraft(draftId: number): Promise<{ discarded: number }> {
  return request(`/pricing/drafts/${draftId}`, { method: 'DELETE' });
}

// --- admin: all bids --------------------------------------------------------

export interface AdminBidRow {
  id: string;
  selected_package: string | null;
  essential_total_cents: number;
  signature_total_cents: number;
  showcase_total_cents: number;
  season_label: string;
  total_cents: number;
  adjustment_total_cents: number;
  est_material_cost_cents: number;
  margin_pct: string | null;
  status: string;
  needs_review: boolean;
  quoted_on_superseded: boolean;
  engine_version: string;
  pricing_version_id: number;
  created_at: string;
  deleted_at: string | null;
  created_by_email: string;
  customer_name: string | null;
  address_line1: string | null;
}

export async function fetchAllBids(includeDeleted = false): Promise<AdminBidRow[]> {
  return request(`/bids/all?includeDeleted=${includeDeleted ? '1' : '0'}`);
}

export type DeleteBidOutcome =
  | { action: 'deleted'; id: string; totalCents: number }
  | { action: 'soft-deleted'; id: string; totalCents: number; status: string; reason: string };

export async function deleteBid(id: string): Promise<DeleteBidOutcome> {
  return request(`/bids/${id}`, { method: 'DELETE' });
}

export async function restoreBid(id: string): Promise<{ restored: string }> {
  return request(`/bids/${id}/restore`, { method: 'POST' });
}
